import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import type { AsociadoCompleto } from '@/lib/typesNeoCore'

/**
 * Modal de edición FEIS por sección.
 *
 * Una sola pieza de UI maneja las 5 secciones editables del expediente FEIS:
 * Identificación, Datos personales, Domicilio, Actividad económica y
 * Banderas regulatorias. Cada sección define su propio set de campos.
 *
 * Estrategia:
 *   - Pre-cargo el form con los valores actuales del FEIS.
 *   - Al guardar, hago diff con los valores originales y solo mando al RPC
 *     los campos que cambiaron (evita updates ruidosos en el histórico).
 *   - El RPC `actualizar_feis(p_asociado_id, p_campos jsonb, p_motivo text)`
 *     ya existe desde la migración 0014 y maneja audit + histórico.
 */

export type FeisSeccion =
  | 'identificacion'
  | 'datos_personales'
  | 'domicilio'
  | 'actividad'
  | 'banderas'

const TITULOS: Record<FeisSeccion, string> = {
  identificacion: 'Identificación',
  datos_personales: 'Datos personales',
  domicilio: 'Domicilio',
  actividad: 'Actividad económica',
  banderas: 'Banderas regulatorias',
}

type Feis = NonNullable<AsociadoCompleto['feis']>

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  seccion: FeisSeccion | null
  feis: Feis | null
  onSuccess: () => void
}

const TIPOS_DOC = ['DPI', 'PASAPORTE', 'EXTRANJERO'] as const
const ESTADOS_CIVIL = ['SOLTERO', 'CASADO', 'UNIDO', 'DIVORCIADO', 'VIUDO', 'SEPARADO'] as const
const ORIGEN_FONDOS = ['SALARIO', 'NEGOCIO_PROPIO', 'PROFESION_INDEPENDIENTE', 'REMESAS', 'INVERSIONES', 'PENSION', 'HERENCIA', 'OTRO'] as const
const SEXOS = ['MASCULINO', 'FEMENINO'] as const

export function EditarFeisSeccionModal({ open, onClose, asociadoId, seccion, feis, onSuccess }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [motivo, setMotivo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Snapshot inicial — para hacer diff al guardar
  const initial = useMemo<Record<string, unknown>>(() => {
    if (!feis || !seccion) return {}
    return camposSeccion(seccion, feis)
  }, [feis, seccion])

  useEffect(() => {
    if (!open) return
    setValues(initial)
    setMotivo('')
    setError(null)
  }, [open, initial])

  const setField = (k: string, v: unknown) => setValues((s) => ({ ...s, [k]: v }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Diff: solo mando lo que cambió
    const diff: Record<string, unknown> = {}
    for (const k of Object.keys(values)) {
      if (norm(values[k]) !== norm(initial[k])) {
        diff[k] = values[k] === '' ? null : values[k]
      }
    }

    if (Object.keys(diff).length === 0) {
      setError('No hubo cambios para guardar.')
      return
    }

    setSubmitting(true)
    const { error: rpcErr } = await supabase.rpc('actualizar_feis', {
      p_asociado_id: asociadoId,
      p_campos: diff,
      p_motivo: motivo.trim() || null,
    })
    setSubmitting(false)

    if (rpcErr) {
      setError(`No se pudo guardar: ${rpcErr.message}`)
      return
    }

    onSuccess()
    onClose()
  }

  if (!seccion || !feis) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Editar · ${TITULOS[seccion]}`}
      subtitle="Los cambios quedan registrados en el histórico FEIS con tu usuario y fecha."
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" form="editar-feis-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <form id="editar-feis-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        {seccion === 'identificacion' && (
          <Grid cols={2}>
            <Field label="Tipo de documento">
              <select value={(values.tipo_documento as string) ?? ''} onChange={(e) => setField('tipo_documento', e.target.value)}>
                {TIPOS_DOC.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Número de documento">
              <input value={(values.numero_documento as string) ?? ''} onChange={(e) => setField('numero_documento', e.target.value)} />
            </Field>
            <Field label="Vigencia (YYYY-MM-DD)">
              <input type="date" value={(values.vigencia_documento as string) ?? ''} onChange={(e) => setField('vigencia_documento', e.target.value)} />
            </Field>
            <Field label="Nacionalidad">
              <input value={(values.nacionalidad as string) ?? ''} onChange={(e) => setField('nacionalidad', e.target.value)} />
            </Field>
          </Grid>
        )}

        {seccion === 'datos_personales' && (
          <Grid cols={2}>
            <Field label="Primer nombre *">
              <input value={(values.primer_nombre as string) ?? ''} onChange={(e) => setField('primer_nombre', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Segundo nombre">
              <input value={(values.segundo_nombre as string) ?? ''} onChange={(e) => setField('segundo_nombre', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Otros nombres">
              <input value={(values.otros_nombres as string) ?? ''} onChange={(e) => setField('otros_nombres', e.target.value.toUpperCase())} placeholder="Tercer nombre o más" />
            </Field>
            <Field label="Primer apellido *">
              <input value={(values.primer_apellido as string) ?? ''} onChange={(e) => setField('primer_apellido', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Segundo apellido">
              <input value={(values.segundo_apellido as string) ?? ''} onChange={(e) => setField('segundo_apellido', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Apellido de casada">
              <input value={(values.apellido_casada as string) ?? ''} onChange={(e) => setField('apellido_casada', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Fecha de nacimiento *">
              <input type="date" value={(values.fecha_nacimiento as string) ?? ''} onChange={(e) => setField('fecha_nacimiento', e.target.value)} />
            </Field>
            <Field label="Sexo *">
              <select value={(values.genero as string) ?? ''} onChange={(e) => setField('genero', e.target.value)}>
                <option value="">— Seleccionar —</option>
                {SEXOS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Estado civil *">
              <select value={(values.estado_civil as string) ?? ''} onChange={(e) => setField('estado_civil', e.target.value)}>
                <option value="">— Seleccionar —</option>
                {ESTADOS_CIVIL.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="País de nacimiento *">
              <input value={(values.pais_nacimiento as string) ?? ''} onChange={(e) => setField('pais_nacimiento', e.target.value.toUpperCase())} placeholder="GUATEMALA" />
            </Field>
            <Field label="Departamento de nacimiento *">
              <input value={(values.departamento_nacimiento as string) ?? ''} onChange={(e) => setField('departamento_nacimiento', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Municipio de nacimiento *">
              <input value={(values.municipio_nacimiento as string) ?? ''} onChange={(e) => setField('municipio_nacimiento', e.target.value.toUpperCase())} />
            </Field>
          </Grid>
        )}

        {seccion === 'domicilio' && (
          <>
            <Field label="Dirección completa">
              <input value={(values.direccion_completa as string) ?? ''} onChange={(e) => setField('direccion_completa', e.target.value)} />
            </Field>
            <Grid cols={3}>
              <Field label="Zona">
                <input value={(values.zona as string) ?? ''} onChange={(e) => setField('zona', e.target.value)} />
              </Field>
              <Field label="Colonia">
                <input value={(values.colonia as string) ?? ''} onChange={(e) => setField('colonia', e.target.value.toUpperCase())} />
              </Field>
              <Field label="Teléfono">
                <input value={(values.telefono as string) ?? ''} onChange={(e) => setField('telefono', e.target.value)} />
              </Field>
            </Grid>
            <Grid cols={3}>
              <Field label="Municipio">
                <input value={(values.municipio as string) ?? ''} onChange={(e) => setField('municipio', e.target.value.toUpperCase())} />
              </Field>
              <Field label="Departamento">
                <input value={(values.departamento as string) ?? ''} onChange={(e) => setField('departamento', e.target.value.toUpperCase())} />
              </Field>
              <Field label="País residencia">
                <input value={(values.pais_residencia as string) ?? ''} onChange={(e) => setField('pais_residencia', e.target.value.toUpperCase())} />
              </Field>
            </Grid>
          </>
        )}

        {seccion === 'actividad' && (
          <>
            <Grid cols={2}>
              <Field label="Profesión / oficio">
                <input value={(values.profesion_oficio as string) ?? ''} onChange={(e) => setField('profesion_oficio', e.target.value.toUpperCase())} />
              </Field>
              <Field label="Actividad económica">
                <input value={(values.actividad_economica as string) ?? ''} onChange={(e) => setField('actividad_economica', e.target.value.toUpperCase())} />
              </Field>
              <Field label="Origen de fondos">
                <select value={(values.origen_fondos as string) ?? ''} onChange={(e) => setField('origen_fondos', e.target.value)}>
                  {ORIGEN_FONDOS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Ingreso mensual aprox. (Q)">
                <input type="number" min="0" step="0.01" value={(values.ingresos_mensuales_aprox as string) ?? ''} onChange={(e) => setField('ingresos_mensuales_aprox', e.target.value)} />
              </Field>
            </Grid>
            <Field label="Propósito de la relación">
              <input value={(values.proposito_relacion as string) ?? ''} onChange={(e) => setField('proposito_relacion', e.target.value)} />
            </Field>
          </>
        )}

        {seccion === 'banderas' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontSize: 12, color: 'var(--n-500)', margin: 0, lineHeight: 1.5 }}>
              Marcá las que apliquen. Cualquier bandera activa fuerza expediente FEIC ampliado y revisión de cumplimiento.
            </p>
            <CheckRow
              checked={!!values.es_pep}
              onChange={(v) => setField('es_pep', v)}
              label="Persona Expuesta Políticamente (PEP)"
              hint="Funcionario público actual o ex-funcionario en los últimos 2 años."
            />
            <CheckRow
              checked={!!values.es_cpe}
              onChange={(v) => setField('es_cpe', v)}
              label="Cliente Públicamente Expuesto (CPE)"
              hint="Personalidad pública con riesgo reputacional (artistas, deportistas notorios, líderes religiosos)."
            />
            <CheckRow
              checked={!!values.es_familiar_pep}
              onChange={(v) => setField('es_familiar_pep', v)}
              label="Familiar de PEP"
              hint="Cónyuge, padres, hijos o hermanos de un PEP."
            />
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          <Field label="Motivo del cambio (opcional, pero recomendado)">
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. Corrección por error de captura · Actualización solicitada por el asociado"
              rows={2}
              style={{ resize: 'vertical', minHeight: 50 }}
            />
          </Field>
        </div>
      </form>
    </Modal>
  )
}

// ---------- Helpers internos ----------

function camposSeccion(seccion: FeisSeccion, feis: Feis): Record<string, unknown> {
  switch (seccion) {
    case 'identificacion':
      return {
        tipo_documento: feis.tipo_documento,
        numero_documento: feis.numero_documento,
        vigencia_documento: toIsoDate(feis.vigencia_documento),
        nacionalidad: feis.nacionalidad,
      }
    case 'datos_personales':
      return {
        primer_nombre: feis.primer_nombre ?? '',
        segundo_nombre: feis.segundo_nombre ?? '',
        otros_nombres: feis.otros_nombres ?? '',
        primer_apellido: feis.primer_apellido ?? '',
        segundo_apellido: feis.segundo_apellido ?? '',
        apellido_casada: feis.apellido_casada ?? '',
        fecha_nacimiento: toIsoDate(feis.fecha_nacimiento),
        genero: feis.genero ?? '',
        estado_civil: feis.estado_civil ?? '',
        pais_nacimiento: feis.pais_nacimiento ?? '',
        departamento_nacimiento: feis.departamento_nacimiento ?? '',
        municipio_nacimiento: feis.municipio_nacimiento ?? '',
      }
    case 'domicilio':
      return {
        direccion_completa: feis.direccion_completa ?? '',
        zona: feis.zona ?? '',
        colonia: feis.colonia ?? '',
        municipio: feis.municipio ?? '',
        departamento: feis.departamento ?? '',
        pais_residencia: feis.pais_residencia ?? '',
        telefono: feis.telefono ?? '',
      }
    case 'actividad':
      return {
        profesion_oficio: feis.profesion_oficio ?? '',
        actividad_economica: feis.actividad_economica ?? '',
        origen_fondos: feis.origen_fondos ?? 'SALARIO',
        ingresos_mensuales_aprox: feis.ingresos_mensuales_aprox != null ? String(feis.ingresos_mensuales_aprox) : '',
        proposito_relacion: feis.proposito_relacion ?? '',
      }
    case 'banderas':
      return {
        es_pep: !!feis.es_pep,
        es_cpe: !!feis.es_cpe,
        es_familiar_pep: !!feis.es_familiar_pep,
      }
  }
}

// Normaliza para comparar valores (null/undefined/'' tratan iguales).
function norm(v: unknown): unknown {
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return v
  return String(v).trim()
}

/**
 * Devuelve siempre 'YYYY-MM-DD' o ''. Tolera:
 *   - null / undefined        → ''
 *   - '2030-05-15'            → '2030-05-15' (passthrough)
 *   - '2030-05-15T00:00:00…'  → '2030-05-15' (recorta T y todo lo posterior)
 *   - Cualquier otro          → '' (input date no acepta formato libre)
 *
 * Necesario porque algunos drivers postgres devuelven `date` columns como
 * timestamp string con T00:00:00 si el cliente fuerza zona horaria — y
 * `<input type="date">` solo acepta el formato puro YYYY-MM-DD.
 */
function toIsoDate(v: string | null | undefined): string {
  if (!v) return ''
  const s = String(v)
  // Si trae 'T', cortamos antes
  const cleaned = s.includes('T') ? s.slice(0, s.indexOf('T')) : s
  // Validamos pattern básico
  return /^\d{4}-\d{2}-\d{2}$/.test(cleaned) ? cleaned : ''
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="form-label" style={{ fontSize: 11 }}>{label}</span>
      {children}
    </label>
  )
}

function Grid({ cols, children }: { cols: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12 }}>
      {children}
    </div>
  )
}

function CheckRow({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <label
      style={{
        display: 'flex', gap: 12, padding: '10px 12px',
        background: checked ? 'var(--brand-50)' : 'var(--surface)',
        border: `1px solid ${checked ? 'var(--brand-300, var(--brand-600))' : 'var(--border)'}`,
        borderRadius: 8, cursor: 'pointer',
      }}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>{hint}</span>
      </span>
    </label>
  )
}
